import { supabase } from '../supabase';
import type {
  Cart,
  Collection,
  Image,
  Menu,
  Money,
  Page,
  Product,
  ProductVariant,
  UserProfile,
  UserMarketplaceProducts
} from './types';

/** Table client_kiosk_shopping_cart peut être absente (projet Maison Adji). On évite de casser l'app. */
function isTableMissingError(err: { code?: string; message?: string } | null): boolean {
  if (!err) return false;
  return err.code === 'PGRST205' || (err.message ?? '').includes('schema cache') || (err.message ?? '').includes('Could not find the table');
}

// Simple cache implementation for client-side
const cache = new Map<string, { data: any; expires: number }>();
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

function getCached<T>(key: string): T | null {
  const cached = cache.get(key);
  if (cached && cached.expires > Date.now()) {
    return cached.data as T;
  }
  cache.delete(key);
  return null;
}

function setCache(key: string, data: any, duration = CACHE_DURATION) {
  cache.set(key, {
    data,
    expires: Date.now() + duration
  });
}

// Helper function to convert price to Money format
const formatMoney = (amount: number, currencyCode: string = 'XOF'): Money => ({
  amount: amount.toString(),
  currencyCode
});

// Helper function to convert images array to Image format
const formatImages = (images: any[], productName: string): Image[] => {
  if (!images || !Array.isArray(images)) {
    return [];
  }
  
  return images.map((img, index) => {
    const url = typeof img === 'string' ? img : img.url || img;
    const filename = url.match(/.*\/(.*)\..*/)?.[1] || `image-${index}`;
    return {
      url,
      altText: typeof img === 'object' && img.altText ? img.altText : `${productName} - ${filename}`,
      width: typeof img === 'object' && img.width ? img.width : 800,
      height: typeof img === 'object' && img.height ? img.height : 800
    };
  });
};

// Helper function to create a handle from product name
const createHandle = (name: string, id: string): string => {
  const slug = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  
  // Retourner slug-id pour garantir l'unicité et permettre la recherche par ID
  return slug ? `${slug}-${id}` : id;
};

// Map kiosk_products to Product type
const mapProductFromSupabase = (row: any): Product | undefined => {
  if (!row || !row.is_active) {
    return undefined;
  }

  const images = formatImages(row.images || [], row.name);
  const featuredImage = images[0] || {
    url: '',
    altText: row.name,
    width: 800,
    height: 800
  };

  const defaultVariant: ProductVariant = {
    id: row.id,
    title: 'Default',
    availableForSale: (row.stock_quantity ?? 0) > 0,
    selectedOptions: [],
    price: formatMoney(row.price)
  };

  const variants: ProductVariant[] = [defaultVariant];
  const handle = createHandle(row.name, row.id);

  return {
    id: String(row.id),
    handle,
    availableForSale: (row.stock_quantity ?? 0) > 0,
    title: String(row.name || ''),
    description: String(row.description || ''),
    descriptionHtml: String(row.description || ''),
    options: (() => {
      if (!row.variant_types) return [];
      try {
        // Si variant_types est un tableau d'objets avec {type, values}
        if (Array.isArray(row.variant_types) && row.variant_types.length > 0) {
          if (typeof row.variant_types[0] === 'object' && row.variant_types[0] !== null) {
            return row.variant_types
              .filter((vt: any) => vt !== null && vt !== undefined)
              .map((vt: any) => ({
                id: String(vt.type || vt.name || vt.id || JSON.stringify(vt)),
                name: String(vt.type || vt.name || 'Option'),
                values: Array.isArray(vt.values) 
                  ? vt.values
                      .filter((v: any) => v !== null && v !== undefined)
                      .map((v: any) => {
                        if (typeof v === 'object') {
                          return String(v.value || v.name || JSON.stringify(v));
                        }
                        return String(v);
                      })
                  : []
              }))
              .filter((opt: any) => opt.name && opt.name !== 'Option');
          }
          // Si c'est un tableau de chaînes
          return row.variant_types
            .filter((type: any) => type !== null && type !== undefined)
            .map((type: any) => ({
              id: String(type),
              name: String(type),
              values: []
            }));
        }
      } catch (error) {
        console.error('Error mapping variant_types:', error);
      }
      return [];
    })(),
    priceRange: {
      maxVariantPrice: formatMoney(row.original_price || row.price),
      minVariantPrice: formatMoney(row.price)
    },
    variants,
    featuredImage,
    images,
    seo: {
      title: row.name,
      description: row.description || row.name
    },
    tags: row.tags || [],
    updatedAt: row.updated_at || row.created_at
  };
};

// Map kiosk_categories to Collection type
const mapCollectionFromSupabase = (row: any): Collection | undefined => {
  if (!row || !row.is_active) {
    return undefined;
  }

  return {
    handle: row.id,
    title: row.name,
    description: row.description || '',
    seo: {
      title: row.name,
      description: row.description || row.name
    },
    path: `/collections/${row.id}`,
    updatedAt: row.updated_at || row.created_at
  };
};

// Cookie helpers for client-side
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

function setCookie(name: string, value: string, days = 365) {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${import.meta.env.MODE === 'production' ? ';Secure' : ''}`;
}

// Cart functions
export async function createCart(): Promise<Cart> {
  const cartId = crypto.randomUUID();
  setCookie('cartId', cartId);

  return {
    id: cartId,
    checkoutUrl: `/checkout?cartId=${cartId}`,
    cost: {
      subtotalAmount: formatMoney(0),
      totalAmount: formatMoney(0),
      totalTaxAmount: formatMoney(0)
    },
    lines: [],
    totalQuantity: 0
  };
}

export async function addToCart(
  lines: { merchandiseId: string; quantity: number; selectedVariants?: Record<string, string>; kioskId?: string }[]
): Promise<Cart> {
  const userId = await getCurrentUserId();
  const cartId = getCookie('cartId') || crypto.randomUUID();
  
  if (!getCookie('cartId')) {
    setCookie('cartId', cartId);
  }

  // Si l'utilisateur n'est pas connecté, utiliser le panier local
  if (!userId) {
    const currentCart = getLocalCart() || await createCart();
    const updatedLines = [...currentCart.lines];

    for (const line of lines) {
      const { data: product } = await supabase
        .from('kiosk_products')
        .select('*')
        .eq('id', line.merchandiseId)
        .single();

      if (!product) continue;

      // Calculer le prix final avec les variants
      let finalPrice = product.price;
      if (line.selectedVariants && Object.keys(line.selectedVariants).length > 0) {
        const { data: variants } = await supabase
          .from('kiosk_product_variants')
          .select('price_modifier')
          .eq('product_id', product.id)
          .in('variant_value', Object.values(line.selectedVariants));

        if (variants && variants.length > 0) {
          const totalModifier = variants.reduce((sum, v) => sum + (v.price_modifier || 0), 0);
          finalPrice = product.price + totalModifier;
        }
      }

      // Vérifier si l'article existe déjà
      const existingIndex = updatedLines.findIndex(
        item => item.merchandise.id === line.merchandiseId
      );

      const featuredImage = formatImages(product.images || [], product.name || '')[0] || {
        url: '',
        altText: product.name || '',
        width: 800,
        height: 800
      };

      if (existingIndex >= 0) {
        // Mettre à jour la quantité
        updatedLines[existingIndex].quantity += line.quantity;
        updatedLines[existingIndex].cost.totalAmount = formatMoney(
          Number(finalPrice) * updatedLines[existingIndex].quantity
        );
      } else {
        // Ajouter un nouvel article
        updatedLines.push({
          id: crypto.randomUUID(),
          quantity: line.quantity,
          cost: {
            totalAmount: formatMoney(Number(finalPrice) * line.quantity)
          },
          merchandise: {
            id: product.id,
            title: product.name || '',
            selectedOptions: line.selectedVariants ? Object.entries(line.selectedVariants).map(([name, value]) => ({
              name,
              value: String(value)
            })) : [],
            product: {
              id: product.id,
              handle: product.id,
              title: product.name || '',
              featuredImage
            }
          }
        });
      }
    }

    // Calculer les totaux
    const subtotal = updatedLines.reduce((sum: number, line: any) => 
      sum + Number(line.cost.totalAmount.amount), 0
    );

    const updatedCart: Cart = {
      id: cartId,
      checkoutUrl: `/checkout?cartId=${cartId}`,
      cost: {
        subtotalAmount: formatMoney(subtotal),
        totalAmount: formatMoney(subtotal),
        totalTaxAmount: formatMoney(0)
      },
      lines: updatedLines,
      totalQuantity: updatedLines.reduce((sum: number, line: any) => sum + line.quantity, 0)
    };

    saveLocalCart(updatedCart);
    return updatedCart;
  }

  // Si l'utilisateur est connecté, utiliser Supabase (si la table client_kiosk_shopping_cart existe)
  const { error: probeError } = await supabase.from('client_kiosk_shopping_cart').select('id').limit(1);
  if (probeError && isTableMissingError(probeError)) {
    return await createCart();
  }

  for (const line of lines) {
    const { data: product } = await supabase
      .from('kiosk_products')
      .select(`
        *,
        kiosk_profiles!inner (
          id,
          business_profile_id
        )
      `)
      .eq('id', line.merchandiseId)
      .single();

    if (!product) continue;

    const kioskProfile = product.kiosk_profiles;
    const kioskProfileData = Array.isArray(kioskProfile) ? kioskProfile[0] : kioskProfile;
    if (!kioskProfileData?.business_profile_id) continue;

    const kioskId = line.kioskId || product.kiosk_id;
    const businessProfileId = kioskProfileData.business_profile_id;

    let finalPrice = product.price;
    if (line.selectedVariants && Object.keys(line.selectedVariants).length > 0) {
      const { data: variants } = await supabase
        .from('kiosk_product_variants')
        .select('price_modifier')
        .eq('product_id', product.id)
        .in('variant_value', Object.values(line.selectedVariants));

      if (variants && variants.length > 0) {
        const totalModifier = variants.reduce((sum, v) => sum + (v.price_modifier || 0), 0);
        finalPrice = product.price + totalModifier;
      }
    }

    const { data: existingItem, error: existingError } = await supabase
      .from('client_kiosk_shopping_cart')
      .select('id, quantity')
      .eq('user_id', userId)
      .eq('kiosk_product_id', line.merchandiseId)
      .eq('kiosk_id', kioskId)
      .single();

    if (existingError && isTableMissingError(existingError)) {
      return await createCart();
    }

    if (existingItem) {
      await supabase
        .from('client_kiosk_shopping_cart')
        .update({
          quantity: existingItem.quantity + line.quantity,
          final_price: finalPrice,
          selected_variants: line.selectedVariants || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingItem.id);
    } else {
      await supabase
        .from('client_kiosk_shopping_cart')
        .insert({
          user_id: userId,
          session_id: null,
          kiosk_product_id: line.merchandiseId,
          kiosk_id: kioskId,
          business_profile_id: businessProfileId,
          quantity: line.quantity,
          selected_variants: line.selectedVariants || null,
          final_price: finalPrice
        });
    }
  }

  return await getCart() || await createCart();
}

export async function removeFromCart(lineIds: string[]): Promise<Cart> {
  const userId = await getCurrentUserId();

  // Si l'utilisateur n'est pas connecté, utiliser le panier local
  if (!userId) {
    const currentCart = getLocalCart();
    if (!currentCart) return await createCart();

    const updatedLines = currentCart.lines.filter(line => !lineIds.includes(line.id || ''));
    const subtotal = updatedLines.reduce((sum: number, line: any) => 
      sum + Number(line.cost.totalAmount.amount), 0
    );

    const updatedCart: Cart = {
      ...currentCart,
      lines: updatedLines,
      cost: {
        subtotalAmount: formatMoney(subtotal),
        totalAmount: formatMoney(subtotal),
        totalTaxAmount: formatMoney(0)
      },
      totalQuantity: updatedLines.reduce((sum: number, line: any) => sum + line.quantity, 0)
    };

    saveLocalCart(updatedCart);
    return updatedCart;
  }

  // Si l'utilisateur est connecté, utiliser Supabase
  if (lineIds.length > 0) {
    const { error } = await supabase
      .from('client_kiosk_shopping_cart')
      .delete()
      .eq('user_id', userId)
      .in('id', lineIds);
    if (error && isTableMissingError(error)) return getLocalCart() ?? (await createCart());
  }

  // Récupérer le panier mis à jour
  return await getCart() || await createCart();
}

export async function updateCart(
  lines: { id: string; merchandiseId: string; quantity: number; selectedVariants?: Record<string, string> }[]
): Promise<Cart> {
  const userId = await getCurrentUserId();

  // Si l'utilisateur n'est pas connecté, utiliser le panier local
  if (!userId) {
    const currentCart = getLocalCart();
    if (!currentCart) return await createCart();

    const updatedLines = currentCart.lines.map(line => {
      const updateLine = lines.find(l => l.id === line.id);
      if (!updateLine) return line;

      if (updateLine.quantity <= 0) {
        return null; // Supprimer
      }

      // Recalculer le prix
      const unitPrice = Number(line.cost.totalAmount.amount) / line.quantity;
      return {
        ...line,
        quantity: updateLine.quantity,
        cost: {
          totalAmount: formatMoney(unitPrice * updateLine.quantity)
        },
        merchandise: {
          ...line.merchandise,
          selectedOptions: updateLine.selectedVariants 
            ? Object.entries(updateLine.selectedVariants).map(([name, value]) => ({
                name,
                value: String(value)
              }))
            : line.merchandise.selectedOptions
        }
      };
    }).filter((line): line is typeof lines[0] & { id: string; quantity: number; cost: { totalAmount: Money }; merchandise: any } => line !== null) as any;

    const subtotal = updatedLines.reduce((sum: number, line: any) => 
      sum + Number(line.cost.totalAmount.amount), 0
    );

    const updatedCart: Cart = {
      ...currentCart,
      lines: updatedLines,
      cost: {
        subtotalAmount: formatMoney(subtotal),
        totalAmount: formatMoney(subtotal),
        totalTaxAmount: formatMoney(0)
      },
      totalQuantity: updatedLines.reduce((sum: number, line: any) => sum + line.quantity, 0)
    };

    saveLocalCart(updatedCart);
    return updatedCart;
  }

  // Si l'utilisateur est connecté, utiliser Supabase
  for (const line of lines) {
    if (line.quantity <= 0) {
      const { error } = await supabase.from('client_kiosk_shopping_cart').delete().eq('id', line.id).eq('user_id', userId);
      if (error && isTableMissingError(error)) return getLocalCart() ?? (await createCart());
    } else {
      const { error } = await supabase
        .from('client_kiosk_shopping_cart')
        .update({
          quantity: line.quantity,
          selected_variants: line.selectedVariants || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', line.id)
        .eq('user_id', userId);
      if (error && isTableMissingError(error)) return getLocalCart() ?? (await createCart());
    }
  }

  // Récupérer le panier mis à jour
  return await getCart() || await createCart();
}

// Helper function to get current user ID (if authenticated) or null
async function getCurrentUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  } catch (error) {
    return null;
  }
}

// Helper functions for local storage cart (for anonymous users)
function getLocalCart(): Cart | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const cartJson = localStorage.getItem('localCart');
    if (!cartJson) return undefined;
    return JSON.parse(cartJson);
  } catch (error) {
    console.error('Error reading local cart:', error);
    return undefined;
  }
}

function saveLocalCart(cart: Cart): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('localCart', JSON.stringify(cart));
  } catch (error) {
    console.error('Error saving local cart:', error);
  }
}

export async function getCart(): Promise<Cart | undefined> {
  const userId = await getCurrentUserId();

  // Si l'utilisateur n'est pas connecté, utiliser le panier local
  if (!userId) {
    return getLocalCart();
  }

  // Si l'utilisateur est connecté, récupérer depuis Supabase
  const { data: cartItems, error } = await supabase
    .from('client_kiosk_shopping_cart')
    .select(`
      id,
      quantity,
      selected_variants,
      final_price,
      kiosk_product_id,
      kiosk_id,
      business_profile_id,
      kiosk_products (
        id,
        name,
        description,
        price,
        images,
        kiosk_id,
        kiosk_profiles (
          id,
          business_name,
          business_profile_id,
          user_id
        )
      )
    `)
    .eq('user_id', userId);

  if (error) {
    if (isTableMissingError(error)) return getLocalCart();
    console.error('Error fetching cart:', error);
    return getLocalCart();
  }

  if (!cartItems || cartItems.length === 0) {
    return undefined;
  }

  // Récupérer le username du propriétaire du kiosk depuis profiles (kiosk_profiles.user_id → profiles.username)
  let kioskOwnerUsername: string | undefined;
  const kioskOwnerIdToUsername = new Map<string, string>();
  const kioskOwnerIds = new Set<string>();
  for (const item of cartItems as any[]) {
    const product = item.kiosk_products;
    if (!product) continue;
    const kp = Array.isArray(product.kiosk_profiles) ? product.kiosk_profiles[0] : product.kiosk_profiles;
    if (kp?.user_id) kioskOwnerIds.add(kp.user_id);
  }
  if (kioskOwnerIds.size > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username')
      .in('id', [...kioskOwnerIds]);
    for (const p of profiles || []) {
      if (p.username) kioskOwnerIdToUsername.set(p.id, p.username);
    }
    const firstEntry = kioskOwnerIdToUsername.values().next();
    if (!firstEntry.done) kioskOwnerUsername = firstEntry.value;
  }

  // Construire les lignes du panier (avec storeUsername / storeName pour multi-checkout)
  const lines = [];
  let subtotal = 0;

  for (const item of cartItems) {
    const product = item.kiosk_products as any;
    if (!product) continue;

    const productPrice = item.final_price || product.price;
    const lineTotal = Number(productPrice) * item.quantity;
    subtotal += lineTotal;

    const kioskProfileData = Array.isArray(product.kiosk_profiles)
      ? product.kiosk_profiles[0]
      : product.kiosk_profiles;
    const storeName = kioskProfileData?.business_name || '';
    const storeUsername = (kioskProfileData?.user_id && kioskOwnerIdToUsername.get(kioskProfileData.user_id)) || '';
    const storeOwnerBurokUserId = kioskProfileData?.user_id || undefined;
    const kioskId = item.kiosk_id || product.kiosk_id || undefined;

    const featuredImage = formatImages(product.images || [], product.name || '')[0] || {
      url: '',
      altText: product.name || '',
      width: 800,
      height: 800
    };

    lines.push({
      id: item.id,
      quantity: item.quantity,
      cost: {
        totalAmount: formatMoney(lineTotal)
      },
      merchandise: {
        id: product.id,
        title: product.name || '',
        selectedOptions: item.selected_variants
          ? Object.entries(item.selected_variants).map(([name, value]) => ({ name, value: String(value) }))
          : [],
        product: {
          id: product.id,
          handle: product.id,
          title: product.name || '',
          featuredImage
        }
      },
      storeUsername: storeUsername || undefined,
      storeName: storeName || undefined,
      storeOwnerBurokUserId: storeOwnerBurokUserId || undefined,
      kioskId: kioskId || undefined,
      storeType: 'kiosk' as const
    });
  }

  const cartId = getCookie('cartId') || crypto.randomUUID();
  if (!getCookie('cartId')) {
    setCookie('cartId', cartId);
  }

  return {
    id: cartId,
    checkoutUrl: `/checkout?cartId=${cartId}`,
    cost: {
      subtotalAmount: formatMoney(subtotal),
      totalAmount: formatMoney(subtotal),
      totalTaxAmount: formatMoney(0)
    },
    lines,
    totalQuantity: lines.reduce((sum, line) => sum + line.quantity, 0),
    kioskOwnerUsername
  };
}

// Collection functions
export async function getCollection(handle: string): Promise<Collection | undefined> {
  const cacheKey = `collection:${handle}`;
  const cached = getCached<Collection>(cacheKey);
  if (cached) return cached;

  const { data, error } = await supabase
    .from('kiosk_categories')
    .select('*')
    .eq('id', handle)
    .eq('is_active', true)
    .single();

  if (error || !data) {
    return undefined;
  }

  const collection = mapCollectionFromSupabase(data);
  if (collection) {
    setCache(cacheKey, collection);
  }
  return collection;
}

export async function getCollectionProducts({
  collection,
  reverse,
  sortKey,
  marketplaceType = 'kiosk'
}: {
  collection: string;
  reverse?: boolean;
  sortKey?: string;
  marketplaceType?: 'kiosk' | 'boutique' | 'restaurant';
}): Promise<Product[]> {
  // Déterminer la table selon le type de marketplace
  const tableName = 
    marketplaceType === 'kiosk' ? 'kiosk_products' :
    marketplaceType === 'boutique' ? 'boutique_products' :
    'restaurant_products';

  const cacheKey = `collection-products:${marketplaceType}:${collection}:${sortKey}:${reverse}`;
  const cached = getCached<Product[]>(cacheKey);
  if (cached) return cached;

  let query = supabase
    .from(tableName)
    .select('*')
    .eq('is_active', true);

  if (collection) {
    query = query.eq('category_id', collection);
  }

  if (sortKey === 'CREATED_AT' || sortKey === 'CREATED') {
    query = query.order('created_at', { ascending: !reverse });
  } else if (sortKey === 'PRICE') {
    query = query.order('price', { ascending: !reverse });
  } else {
    query = query.order('updated_at', { ascending: !reverse });
  }

  const { data, error } = await query;

  if (error || !data) {
    console.log(`No products found for collection \`${collection}\``);
    return [];
  }

  const products = data.map(mapProductFromSupabase).filter((p): p is Product => p !== undefined);
  setCache(cacheKey, products);
  return products;
}

export async function getCollections(): Promise<Collection[]> {
  const cacheKey = 'collections:all';
  const cached = getCached<Collection[]>(cacheKey);
  if (cached) return cached;

  const { data } = await supabase
    .from('kiosk_categories')
    .select('*')
    .eq('is_active', true)
    .order('name');

  const collections = [
    {
      handle: '',
      title: 'All',
      description: 'All products',
      seo: {
        title: 'All',
        description: 'All products'
      },
      path: '/collections',
      updatedAt: new Date().toISOString()
    }
  ];

  if (data) {
    collections.push(
      ...data
        .map(mapCollectionFromSupabase)
        .filter((c): c is Collection => c !== undefined)
    );
  }

  setCache(cacheKey, collections);
  return collections;
}

// Menu functions
export async function getMenu(_handle: string): Promise<Menu[]> {
  return [];
}

// Page functions
export async function getPage(handle: string): Promise<Page> {
  return {
    id: handle,
    title: '',
    handle,
    body: '',
    bodySummary: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

export async function getPages(): Promise<Page[]> {
  return [];
}

// Product functions
export async function getProduct(
  handle: string,
  marketplaceType: 'kiosk' | 'boutique' | 'restaurant' = 'kiosk'
): Promise<Product | undefined> {
  // Déterminer la table selon le type de marketplace
  const tableName = 
    marketplaceType === 'kiosk' ? 'kiosk_products' :
    marketplaceType === 'boutique' ? 'boutique_products' :
    'restaurant_products';

  const cacheKey = `product:${marketplaceType}:${handle}`;
  const cached = getCached<Product>(cacheKey);
  if (cached) return cached;

  let query = supabase
    .from(tableName)
    .select('*')
    .eq('is_active', true);

  // Vérifier si le handle contient un UUID à la fin (format: slug-uuid)
  const uuidRegex = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
  const uuidMatch = handle.match(uuidRegex);
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(handle);
  
  // Si on trouve un UUID dans le handle, chercher directement par ID
  if (uuidMatch) {
    const productId = uuidMatch[1];
    query = query.eq('id', productId);
  } else if (isUUID) {
    // Si le handle est juste un UUID
    query = query.eq('id', handle);
  } else {
    // Sinon, chercher par le handle (slug)
    const { data: allProducts } = await supabase
      .from(tableName)
      .select('*')
      .eq('is_active', true);
    
    if (allProducts) {
      const product = allProducts.find(p => {
        const productHandle = createHandle(p.name, p.id);
        return productHandle === handle || `${productHandle}-${p.id}` === handle;
      });
      
      if (product) {
        const mapped = mapProductFromSupabase(product);
        if (mapped) setCache(cacheKey, mapped);
        return mapped;
      }
    }
    return undefined;
  }

  const { data, error } = await query.single();

  if (error || !data) {
    return undefined;
  }

  const product = mapProductFromSupabase(data);
  if (product) {
    setCache(cacheKey, product);
  }
  return product;
}

export async function getProductRecommendations(
  productId: string,
  marketplaceType: 'kiosk' | 'boutique' | 'restaurant' = 'kiosk'
): Promise<Product[]> {
  // Déterminer la table selon le type de marketplace
  const tableName = 
    marketplaceType === 'kiosk' ? 'kiosk_products' :
    marketplaceType === 'boutique' ? 'boutique_products' :
    'restaurant_products';

  const cacheKey = `product-recommendations:${marketplaceType}:${productId}`;
  const cached = getCached<Product[]>(cacheKey);
  if (cached) return cached;

  const { data: product } = await supabase
    .from(tableName)
    .select('category_id')
    .eq('id', productId)
    .single();

  if (!product?.category_id) {
    return [];
  }

  const { data: recommendations } = await supabase
    .from(tableName)
    .select('*')
    .eq('category_id', product.category_id)
    .eq('is_active', true)
    .neq('id', productId)
    .limit(4);

  if (!recommendations) {
    return [];
  }

  const products = recommendations
    .map(mapProductFromSupabase)
    .filter((p): p is Product => p !== undefined);
  
  setCache(cacheKey, products);
  return products;
}

export async function getProducts({
  query,
  reverse,
  sortKey,
  marketplaceType = 'kiosk'
}: {
  query?: string;
  reverse?: boolean;
  sortKey?: string;
  marketplaceType?: 'kiosk' | 'boutique' | 'restaurant';
}): Promise<Product[]> {
  // Déterminer la table selon le type de marketplace
  const tableName = 
    marketplaceType === 'kiosk' ? 'kiosk_products' :
    marketplaceType === 'boutique' ? 'boutique_products' :
    'restaurant_products';

  const cacheKey = `products:${marketplaceType}:${query || 'all'}:${sortKey}:${reverse}`;
  const cached = getCached<Product[]>(cacheKey);
  if (cached) return cached;

  let supabaseQuery = supabase
    .from(tableName)
    .select('*')
    .eq('is_active', true);

  if (query) {
    supabaseQuery = supabaseQuery.or(
      `name.ilike.%${query}%,description.ilike.%${query}%,search_keywords.ilike.%${query}%`
    );
  }

  if (sortKey === 'CREATED_AT' || sortKey === 'CREATED') {
    supabaseQuery = supabaseQuery.order('created_at', { ascending: !reverse });
  } else if (sortKey === 'PRICE') {
    supabaseQuery = supabaseQuery.order('price', { ascending: !reverse });
  } else if (sortKey === 'BEST_SELLING') {
    supabaseQuery = supabaseQuery.order('order_count', { ascending: false });
  } else {
    supabaseQuery = supabaseQuery.order('updated_at', { ascending: !reverse });
  }

  const { data, error } = await supabaseQuery;

  if (error || !data) {
    return [];
  }

  const products = data
    .map(mapProductFromSupabase)
    .filter((p): p is Product => p !== undefined);
  
  setCache(cacheKey, products);
  return products;
}

// User Profile functions
export async function getProfileByUsername(username: string): Promise<UserProfile | undefined> {
  const cacheKey = `profile:${username}`;
  const cached = getCached<UserProfile>(cacheKey);
  if (cached) return cached;

  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, first_name, last_name, avatar_url, banner_image_url, bio, instagram_username, twitter_username, tiktok_username, snapchat_username, linkedin_username, youtube_username, facebook_username')
    .eq('username', username)
    .eq('is_public_profile', true)
    .single();

  if (error || !data) {
    return undefined;
  }

  const profile: UserProfile = {
    id: data.id,
    username: data.username,
    first_name: data.first_name,
    last_name: data.last_name,
    avatar_url: data.avatar_url,
    banner_image_url: data.banner_image_url,
    bio: data.bio,
    instagram_username: data.instagram_username,
    twitter_username: data.twitter_username,
    tiktok_username: data.tiktok_username,
    snapchat_username: data.snapchat_username,
    linkedin_username: data.linkedin_username,
    youtube_username: data.youtube_username,
    facebook_username: data.facebook_username
  };

  setCache(cacheKey, profile);
  return profile;
}

export async function getUserProducts(userId: string): Promise<UserMarketplaceProducts> {
  const cacheKey = `user-products:${userId}`;
  const cached = getCached<UserMarketplaceProducts>(cacheKey);
  if (cached) return cached;

  // Récupérer les IDs des profils pour chaque marketplace
  const { data: kioskProfiles } = await supabase
    .from('kiosk_profiles')
    .select('id')
    .eq('user_id', userId)
    .eq('is_active', true)
    .eq('is_live', true);

  const { data: boutiqueProfiles } = await supabase
    .from('boutique_profiles')
    .select('id')
    .eq('user_id', userId)
    .eq('is_active', true)
    .eq('is_live', true);

  const { data: restaurantProfiles } = await supabase
    .from('restaurant_profiles')
    .select('id')
    .eq('user_id', userId)
    .eq('is_active', true)
    .eq('is_live', true);

  const kioskIds = kioskProfiles?.map(p => p.id) || [];
  const boutiqueIds = boutiqueProfiles?.map(p => p.id) || [];
  const restaurantIds = restaurantProfiles?.map(p => p.id) || [];

  // Récupérer les produits pour chaque marketplace
  const [kioskData, boutiqueData, restaurantData] = await Promise.all([
    kioskIds.length > 0
      ? supabase
          .from('kiosk_products')
          .select('*')
          .eq('is_active', true)
          .in('kiosk_id', kioskIds)
          .limit(10)
      : { data: [], error: null },
    
    boutiqueIds.length > 0
      ? supabase
          .from('boutique_products')
          .select('*')
          .eq('is_active', true)
          .in('boutique_id', boutiqueIds)
          .limit(10)
      : { data: [], error: null },
    
    restaurantIds.length > 0
      ? supabase
          .from('restaurant_products')
          .select('*')
          .eq('is_active', true)
          .in('restaurant_id', restaurantIds)
          .limit(10)
      : { data: [], error: null }
  ]);

  const result: UserMarketplaceProducts = {
    kiosk: (kioskData.data || []).map(row => mapProductFromSupabase(row)).filter((p): p is Product => p !== undefined),
    boutique: (boutiqueData.data || []).map(row => mapProductFromSupabase(row)).filter((p): p is Product => p !== undefined),
    restaurant: (restaurantData.data || []).map(row => mapProductFromSupabase(row)).filter((p): p is Product => p !== undefined)
  };

  setCache(cacheKey, result);
  return result;
}

